import React, { useState, useEffect } from 'react';
import { Save, Check, CircleMinus, CircleCheck } from 'lucide-react';
import FormControl from '../common/FormControl';
import Modal from '../common/Modal';
import { PlanPeriodicity, PlanYearType } from '../../services/auditPlanService';
import AgencyPlanWindowFields, { PlanWindowValue } from './AgencyPlanWindowFields';
import Button from '../common/Button';
import { ValidatedInput, ValidatedTextarea } from '../../validation/ValidatedFields';
import { useFieldValidation } from '../../validation/useFieldValidation';
import { isUniqueLocal } from '../../validation/uniqueness';
import { confirmWarning, confirmActive } from '../common/confirm';
import { Agency, AgencyCreatePayload, AgencyLinkCandidate, agencyMemberService } from '../../services/auditService';
import { userService } from '../../services/userService';
import { usePermission, NOT_AUTHORISED_MSG } from '../../contexts/PermissionContext';

interface AgencyModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (data: AgencyCreatePayload, id?: number) => void | Promise<void>;
    initialData: Agency | null;
    /** Soft-delete: deactivate this agency (edit mode only). Parent owns confirm + reload + toast. */
    onInactivate?: (id: number) => Promise<void>;
    /** Soft-delete: reactivate this agency (edit mode only). Parent owns confirm + reload + toast. */
    onReactivate?: (id: number) => Promise<void>;
}

type HeadMode = 'link' | 'create';

interface DropdownUser { id: number; name: string; email: string; }

const AgencyModal: React.FC<AgencyModalProps> = ({
    isOpen, onClose, onSave, initialData, onInactivate, onReactivate,
}) => {
    const isEdit = !!initialData;
    const isCurrentlyActive = (initialData?.is_active ?? 1) === 1;
    // Approved agencies are frozen: read-only, no deactivate. Reopen via the status dot.
    const isApproved = isEdit && (initialData as any)?.workflow_state === 'approved';

    const fieldVh = useFieldValidation();
    const { allowed: canModify, loading: permLoading } = usePermission('ADT_AGENCIES', 'view+modify');

    const [agencyName, setAgencyName] = useState('');
    const [description, setDescription] = useState('');
    const [isGlobal, setIsGlobal] = useState(0);
    const [planWindow, setPlanWindow] = useState<PlanWindowValue>({ periodicity: 'QUARTERLY', yearType: 'FINANCIAL', startId: null, endId: null });
    // Internal/External character — set once at creation, immutable thereafter.
    const [agencyKind, setAgencyKind] = useState<'Internal' | 'External'>('External');

    const [headMode, setHeadMode] = useState<HeadMode>('link');
    const [headUserId, setHeadUserId] = useState<number | ''>('');
    const [headName, setHeadName] = useState('');
    const [headEmail, setHeadEmail] = useState('');
    const [headPassword, setHeadPassword] = useState('');

    const [users, setUsers] = useState<DropdownUser[]>([]);
    // Head-picker options, filtered by agency character (External → External
    // Auditors only; Internal → internal staff). Distinct from `users`, which is
    // kept only to resolve the current-head label in edit mode.
    const [candidates, setCandidates] = useState<AgencyLinkCandidate[]>([]);
    const [usersLoading, setUsersLoading] = useState(false);

    const [submitting, setSubmitting] = useState(false);
    const [toggling, setToggling] = useState(false);

    // Current-head label (edit only) needs the full directory.
    useEffect(() => {
        if (!isOpen || !isEdit) return;
        let cancelled = false;
        userService.getUsersDropdown()
            .then(rows => { if (!cancelled) setUsers(rows); })
            .catch(() => { if (!cancelled) setUsers([]); });
        return () => { cancelled = true; };
    }, [isOpen, isEdit]);

    // Head-picker candidates, kind-filtered. Create → by chosen character
    // (no agency_id yet); Edit → this agency's link-candidates. Re-fetches when
    // the character toggles so an External agency never lists internal users.
    useEffect(() => {
        if (!isOpen) return;
        let cancelled = false;
        setUsersLoading(true);
        const load = isEdit && initialData
            ? agencyMemberService.linkCandidates(initialData.id)
            : agencyMemberService.headCandidatesByKind(agencyKind);
        load
            .then(rows => { if (!cancelled) setCandidates(rows); })
            .catch(() => { if (!cancelled) setCandidates([]); })
            .finally(() => { if (!cancelled) setUsersLoading(false); });
        return () => { cancelled = true; };
    }, [isOpen, isEdit, agencyKind, initialData]);

    useEffect(() => {
        if (initialData) {
            setAgencyName(initialData.audit_agency_name ?? '');
            setDescription(initialData.description ?? '');
            setIsGlobal(initialData.is_global ?? 0);
            setPlanWindow({
                periodicity: (initialData.plan_periodicity as PlanPeriodicity) ?? 'QUARTERLY',
                yearType: (initialData.plan_year_type as PlanYearType) ?? 'FINANCIAL',
                startId: initialData.plan_start_period_id ?? null,
                endId: initialData.plan_end_period_id ?? null,
            });
            setAgencyKind((initialData.agency_kind as 'Internal' | 'External') ?? 'External');
            setHeadMode('link');
            setHeadUserId(initialData.head_user_id ?? '');
            setHeadName(''); setHeadEmail(''); setHeadPassword('');
        } else {
            setAgencyName(''); setDescription(''); setIsGlobal(0);
            setAgencyKind('External');
            setHeadMode('link'); setHeadUserId('');
            setHeadName(''); setHeadEmail(''); setHeadPassword('');
        }
        setSubmitting(false);
        setToggling(false);
        fieldVh.reset();
    }, [initialData, isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

    const nameOk = agencyName.trim().length > 0;
    const linkOk = headMode === 'link' && headUserId !== '';
    const createOk = headMode === 'create'
        && headName.trim().length > 0
        && /^\S+@\S+\.\S+$/.test(headEmail.trim())
        && headPassword.length >= 4;

    // On Edit: re-picking a Head is optional (you can update name/desc/is_global alone).
    // On Create: a Head is mandatory — either link OR create.
    const headOk = isEdit ? true : (linkOk || createOk);
    const canSave = nameOk && headOk && planWindow.startId != null && Object.keys(fieldVh.errors).length === 0;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!canModify) return;
        const engineOk = fieldVh.validateAll([
            { name: 'agencyName', code: 109, value: agencyName, required: true },
            { name: 'description', code: 110, value: description },
            ...(headMode === 'create' ? [
                { name: 'headName', code: 109, value: headName, required: true },
                { name: 'headEmail', code: 401, value: headEmail, required: true },
            ] : []),
        ]);
        if (!engineOk) return;

        const payload: AgencyCreatePayload = {
            audit_agency_name: agencyName.trim(),
            description: description || null,
            is_global: isGlobal,
            plan_periodicity: planWindow.periodicity,
            plan_year_type: planWindow.yearType,
            plan_start_period_id: planWindow.startId,
            plan_end_period_id: planWindow.endId,
            ...(planWindow.endId == null ? { clear_plan_end_period: true } : {}),
        };

        if (isEdit) {
            // Only send head_user_id if user actually picked someone (and only on link mode)
            if (headMode === 'link' && headUserId !== '' && headUserId !== initialData?.head_user_id) {
                payload.head_user_id = Number(headUserId);
            }
        } else {
            // Character is set only at creation (backend ignores it on update).
            payload.agency_kind = agencyKind;
            if (headMode === 'link') {
                payload.head_user_id = Number(headUserId);
            } else {
                payload.head_name = headName.trim();
                payload.head_email = headEmail.trim();
                payload.head_password = headPassword;
            }
        }

        setSubmitting(true);
        try {
            await onSave(payload, initialData?.id);
        } finally {
            setSubmitting(false);
        }
    };

    const handleToggleActive = async () => {
        if (!initialData || isApproved) return;
        const confirmFn = isCurrentlyActive ? confirmWarning : confirmActive;
        const ok = await confirmFn({
            title: isCurrentlyActive ? 'Inactivate Agency?' : 'Reactivate Agency?',
            text: isCurrentlyActive
                ? 'The record will be retained but marked Inactive.'
                : 'The agency will become active again.',
            confirmText: isCurrentlyActive ? 'Yes, Inactivate' : 'Yes, Reactivate',
        });
        if (!ok) return;
        setToggling(true);
        try {
            if (isCurrentlyActive) {
                await onInactivate?.(initialData.id);
            } else {
                await onReactivate?.(initialData.id);
            }
        } finally {
            setToggling(false);
        }
    };

    const currentHeadLabel = (() => {
        if (!initialData?.head_user_id) return null;
        const u = users.find(x => x.id === initialData.head_user_id);
        return u ? `${u.name} (${u.email})` : `User #${initialData.head_user_id}`;
    })();

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="md">
            <Modal.Header>
                {isEdit
                    ? <>Editing "<span className="item-highlight">{initialData!.audit_agency_name}</span>"</>
                    : 'Create Audit Agency'}
            </Modal.Header>
            <Modal.Body>
                <form id="agency-form" onSubmit={handleSubmit} className="flex flex-col gap-y-4" data-testid="MOD_agency_form">
                    {/* System code (ADT-N) — auto-generated on approve, read-only.
                        Only shown when editing an existing record; null before approval. */}
                    {isEdit && (
                        <FormControl.Input
                            label="Code"
                            value={(initialData as any)?.code || '—'}
                            disabled
                            data-testid="INP_agency_code"
                        />
                    )}
                    <fieldset disabled={isApproved} className="contents" data-testid="FS_agency_lock">
                    <ValidatedInput
                        vh={fieldVh}
                        name="agencyName"
                        code={109}
                        required
                        label="Agency Name"
                        value={agencyName}
                        onChange={e => setAgencyName(e.target.value)}
                        placeholder="e.g. Deloitte & Touche"
                        disabled={isApproved}
                        data-testid="INP_agency_name"
                    />
                    <ValidatedTextarea
                        vh={fieldVh}
                        name="description"
                        code={110}
                        label="Description"
                        value={description}
                        onChange={e => setDescription(e.target.value)}
                        placeholder="Optional — brief description of this agency"
                        rows={3}
                        disabled={isApproved}
                        data-testid="INP_agency_description"
                    />

                    {/* Agency Type FIRST — chosen before the Head, so the head
                        picker can filter to the matching (External/Internal) users. */}
                    <FormControl.SearchSelect
                        label="Agency Type"
                        required={!isEdit}
                        value={agencyKind}
                        onChange={v => {
                            if (v == null) return;
                            setAgencyKind(v as 'Internal' | 'External');
                            // Candidate pools are disjoint (External Auditors vs
                            // internal staff) — a head picked under the other kind
                            // would silently ride along while the picker looks blank.
                            setHeadUserId('');
                        }}
                        disabled={isEdit || isApproved}
                        helper={isEdit
                            ? 'Set at creation — cannot be changed.'
                            : 'Determines the audit role assigned to linked users.'}
                        options={[
                            { value: 'External', label: 'External — outside auditors (External Auditor role)' },
                            { value: 'Internal', label: 'Internal — entity staff (Internal Audit Team role)' },
                        ]}
                        data-testid="SEL_agency_kind"
                    />

                    {isEdit && currentHeadLabel && (
                        <div className="text-xs text-gray-500">
                            Current Head: <span className="font-medium text-gray-700">{currentHeadLabel}</span>
                        </div>
                    )}

                    {!isEdit && (
                        <FormControl.RadioGroup
                            name="head-mode"
                            inline
                            value={headMode}
                            onChange={v => {
                                setHeadMode(v as HeadMode);
                                // The abandoned mode's head fields unmount with any
                                // blur errors still recorded; canSave counts every
                                // error, so Submit would stay disabled with nothing
                                // visibly wrong.
                                fieldVh.clearError('headName');
                                fieldVh.clearError('headEmail');
                            }}
                            options={[
                                { value: 'link', label: 'Link existing user' },
                                { value: 'create', label: 'Create new user' },
                            ]}
                        />
                    )}

                    {headMode === 'link' && (
                        <FormControl.SearchSelect
                            label={isEdit ? 'Change Head (Point of Contact)' : 'Head (Point of Contact)'}
                            required={!isEdit}
                            clearable
                            disabled={isApproved}
                            placeholder={usersLoading ? 'Loading…' : '— select a user —'}
                            value={headUserId === '' ? null : headUserId}
                            onChange={v => setHeadUserId(v == null ? '' : Number(v))}
                            options={candidates.map(u => ({
                                value: u.user_id,
                                label: `${u.name}${u.email ? ` (${u.email})` : ''}`,
                            }))}
                            data-testid="SEL_agency_head_user"
                        />
                    )}

                    {!isEdit && headMode === 'create' && (
                        <div className="form-grid form-grid--3col">
                            <ValidatedInput
                                vh={fieldVh}
                                name="headName"
                                code={109}
                                required
                                label="Head (Point of Contact) Name"
                                value={headName}
                                onChange={e => setHeadName(e.target.value)}
                                placeholder="e.g. John Doe"
                                disabled={isApproved}
                                data-testid="INP_agency_head_name"
                            />
                            <ValidatedInput
                                vh={fieldVh}
                                name="headEmail"
                                code={401}
                                required
                                type="email"
                                label="Head Email"
                                value={headEmail}
                                onChange={e => setHeadEmail(e.target.value)}
                                placeholder="head@example.com"
                                disabled={isApproved}
                                data-testid="INP_agency_head_email"
                            />
                            <FormControl.Input
                                label="Password"
                                type="password"
                                required
                                value={headPassword}
                                onChange={e => setHeadPassword(e.target.value)}
                                placeholder="Min. 4 characters"
                                disabled={isApproved}
                                data-testid="INP_agency_head_password"
                            />
                        </div>
                    )}

                    <AgencyPlanWindowFields value={planWindow} onChange={setPlanWindow} disabled={isApproved} testIdPrefix="SEL_agency" />

                    <FormControl.Checkbox
                        label="Is Global (visible to all entities)"
                        checked={isGlobal === 1}
                        onChange={checked => setIsGlobal(checked ? 1 : 0)}
                        disabled={isApproved}
                        data-testid="CHK_agency_is_global"
                    />
                    </fieldset>
                </form>
            </Modal.Body>
            <Modal.Footer className={isEdit && !isApproved ? 'app-modal-footer--split' : ''}>
                {isApproved ? (
                    <span style={{ fontSize: 13, color: '#64748b', fontStyle: 'italic', alignSelf: 'center' }} data-testid="LBL_agency_readonly">
                        Approved — read-only. Reopen from the status dot to edit.
                    </span>
                ) : (
                    <>
                        {isEdit && (
                            <Button
                                variant={isCurrentlyActive ? 'inactive-outline' : 'active-outline'}
                                icon={isCurrentlyActive ? CircleMinus : CircleCheck}
                                type="button"
                                onClick={handleToggleActive}
                                disabled={toggling || submitting}
                                data-testid="BTN_agency_toggle_active"
                            >
                                {toggling ? '...' : isCurrentlyActive ? 'Inactive' : 'Active'}
                            </Button>
                        )}
                        <Button
                            type="submit"
                            form="agency-form"
                            variant={isEdit ? 'update' : 'fill'}
                            icon={isEdit ? Check : Save}
                            disabled={permLoading || !canModify || !canSave || submitting || toggling}
                            title={!canModify ? NOT_AUTHORISED_MSG : undefined}
                            data-testid="BTN_agency_submit"
                        >
                            {submitting
                                ? isEdit ? 'Updating...' : 'Submitting...'
                                : isEdit ? 'Update' : 'Submit'}
                        </Button>
                    </>
                )}
            </Modal.Footer>
        </Modal>
    );
};

export default AgencyModal;
